import {assertUnreachable} from '@wandb/weave/common/util/types';

import {TriggerScopeType} from '../../../generated/graphql';

/**
 * The subset of TriggerScopeType values that the frontend currently handles.
 * New values can be added to the backend GraphQL schema without breaking the
 * frontend; add them here when frontend support is ready.
 */
const KNOWN_SCOPE_TYPES = [
  TriggerScopeType.Project,
  TriggerScopeType.ArtifactCollection,
  TriggerScopeType.Entity,
] as const;

export type KnownScopeType = (typeof KNOWN_SCOPE_TYPES)[number];

export function isKnownScopeType(
  scopeType: TriggerScopeType
): scopeType is KnownScopeType {
  return (KNOWN_SCOPE_TYPES as readonly TriggerScopeType[]).includes(scopeType);
}

/**
 * The subset of GraphQL union member typenames in `TriggerScope` that the
 * frontend currently handles. New union members can be added to the backend
 * schema without breaking the frontend; add them here when frontend support
 * is ready.
 */
const ARTIFACT_COLLECTION_SCOPE_TYPENAMES = [
  'ArtifactSequence',
  'ArtifactPortfolio',
] as const;

export type ArtifactCollectionScopeTypename =
  (typeof ARTIFACT_COLLECTION_SCOPE_TYPENAMES)[number];

const KNOWN_SCOPE_TYPENAMES = [
  'Project',
  'Entity',
  ...ARTIFACT_COLLECTION_SCOPE_TYPENAMES,
] as const;

export type KnownScopeTypename = (typeof KNOWN_SCOPE_TYPENAMES)[number];

// -----------------------------------------------------------------------------

export function hasKnownScopeTypename<T extends {__typename: string}>(
  scope: T
): scope is T & {__typename: KnownScopeTypename} {
  return (KNOWN_SCOPE_TYPENAMES as readonly string[]).includes(
    scope.__typename
  );
}

// -----------------------------------------------------------------------------

/**
 * Maps a supported trigger scope to its UI scope type. Returns undefined for scope
 * types the frontend does not support yet.
 */
export function getKnownScopeType<T extends {__typename: string}>(
  scope: T
): KnownScopeType | undefined {
  if (!hasKnownScopeTypename(scope)) {
    return undefined;
  }
  switch (scope.__typename) {
    case 'Project':
      return TriggerScopeType.Project;
    case 'ArtifactSequence':
    case 'ArtifactPortfolio':
      return TriggerScopeType.ArtifactCollection;
    case 'Entity':
      return TriggerScopeType.Entity;
    default:
      assertUnreachable(scope.__typename);
  }
}

export function hasCollectionScopeTypename<T extends {__typename: string}>(
  scope: T
): scope is T & {__typename: ArtifactCollectionScopeTypename} {
  return (ARTIFACT_COLLECTION_SCOPE_TYPENAMES as readonly string[]).includes(
    scope.__typename
  );
}
